import React from 'react';
import { FormControl, MenuItem, Select } from '@mui/material';
import { Box } from '@mui/system';

import { selectMenuProps } from '../common/menu/primitives';
import { SORT_PREFIX_SX, SORT_SELECT_SX } from './styles';

// Points, comments and reposts — what every discussion source ranks by.
const DISCUSSION_SORTS = [
  ['popularity', 'Popularity'],
  ['comments', 'Comments'],
  ['reposts', 'Reposts'],
];

// Per source: the sort options a grid offers, and its fallback when the URL names
// none. `cl` is for item grids only; author, site and list grids on CuratedList
// use ENTITY_SORTS.
const SOURCE_SORTS = {
  cl: {
    fallback: 'recorded',
    options: [
      ['recorded', 'Date Recorded'],
      ['published', 'Date Published'],
      ['reviewed', 'Date Reviewed'],
      ['heat', 'Heat'],
      ['buzz', 'Buzz'],
    ],
  },
  hn: { fallback: 'popularity', options: DISCUSSION_SORTS },
  lb: { fallback: 'popularity', options: DISCUSSION_SORTS },
  rd: { fallback: 'popularity', options: DISCUSSION_SORTS },
  gh: {
    fallback: 'stars',
    options: [
      ['stars', 'Stars'],
      ['forks', 'Forks'],
      ['watchers', 'Watchers'],
      ['issues', 'Issues'],
    ],
  },
  yt: {
    fallback: 'views',
    options: [['views', 'Views'], ['likes', 'Likes'], ['comments', 'Comments']],
  },
  tw: {
    fallback: 'likes',
    options: [['likes', 'Popularity'], ['replies', 'Comments']],
  },
};

// Only a public record carries a share date. The server applies this too.
const SHARED_SORT = ['shared', 'Date Shared'];

const PUBLIC_ITEM_SORTS = {
  fallback: SHARED_SORT[0],
  options: [SHARED_SORT, ...SOURCE_SORTS.cl.options],
};

function itemSorts(context) {
  const source = context.source || 'cl';
  if (source === 'cl' && context.namespace === 'public') return PUBLIC_ITEM_SORTS;
  return SOURCE_SORTS[source];
}

const SCORED_SORTS = [
  ['heat', 'Heat'],
  ['buzz', 'Buzz'],
  ['items', 'Item Count'],
  ['latest', 'Latest Item'],
  ['recorded', 'Recorded Date'],
  ['updated', 'Updated Date'],
];

// Author, site and list grids on CuratedList. On another source they sort by
// that source's SOURCE_SORTS, like an item grid. The server applies this too.
const ENTITY_SORTS = {
  authors: SCORED_SORTS,
  sites: SCORED_SORTS,
  lists: SCORED_SORTS,
};

const ENTITY_FALLBACK = 'latest';

// A named view can lead with an order of its own. The server applies this too.
// It is mirrored here so the control names the order the grid is actually in.
const VIEW_SORTS = { history: 'reviewed' };

// Which options and current value the context calls for, or null where sorting
// doesn't apply. A grid with no source is CuratedList.
function resolveSort(context) {
  const type = context?.type;
  if (type === 'items') {
    const known = itemSorts(context);
    const source = known || SOURCE_SORTS.cl;
    const viewSort = VIEW_SORTS[context.view];
    const offersViewSort = source.options.some(([value]) => value === viewSort);
    return {
      id: 'header-order-by-select',
      options: known ? source.options : [],
      value: context.sortby || (offersViewSort ? viewSort : source.fallback),
    };
  }
  const sourceSorts = context?.source && context.source !== 'cl' && SOURCE_SORTS[context.source];
  if (ENTITY_SORTS[type] && sourceSorts) {
    return {
      id: `header-order-by-select-${type}`,
      options: sourceSorts.options,
      value: context.sortby || sourceSorts.fallback,
    };
  }
  if (ENTITY_SORTS[type]) {
    const isPublic = context.namespace === 'public';
    return {
      id: `header-order-by-select-${type}`,
      options: isPublic ? [SHARED_SORT, ...ENTITY_SORTS[type]] : ENTITY_SORTS[type],
      value: context.sortby || ENTITY_FALLBACK,
    };
  }
  return null;
}

/**
 * The header's "By …" control. Renders nothing for a context that has no sort
 * order of its own.
 */
export default function SortSelect({ context, onChange }) {
  const sort = resolveSort(context);
  if (!sort) return null;

  return (
    <FormControl size="small" sx={{ minWidth: 60, ml: 0.5 }}>
      <Select
        id={sort.id}
        value={sort.value}
        onChange={(e) => onChange(e.target.value)}
        variant="outlined"
        startAdornment={<Box component="span" sx={SORT_PREFIX_SX}>By</Box>}
        sx={SORT_SELECT_SX}
        MenuProps={selectMenuProps}
      >
        {sort.options.map(([value, label]) => (
          <MenuItem key={value} value={value}>{label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
